import React, { useMemo } from 'react';
import {
  AnimatedRecyclerList,
  KeyboardAwareRecyclerList,
  Props as RecyclerListProps,
  RecyclerListRef,
} from '@/components/recycler-list';
import {
  ItemListOption,
  ItemListOptionSection,
} from '@/contexts/item-context-types';
import { KeyboardAwareProps } from '@/components/with-keyboard-aware';
import { LayoutSize, useLayoutSizeValue } from '@/contexts/layout-context';
import {
  Props as ListItemProps,
  ListOptionValue,
} from '@/components/list-item';
import {
  Platform,
  ScrollViewProps,
  StyleProp,
  StyleSheet,
  ViewStyle,
} from 'react-native';
import { useShowListView } from '@/hooks/use-show-list-view';

const LAYOUT_SIZE_TO_COLUMNS_MAP: { [size in LayoutSize]: number } = {
  [LayoutSize.SMALL]: 1,
  [LayoutSize.SMALL_TABLET]: 1,
  [LayoutSize.MEDIUM]: 2,
  [LayoutSize.LARGE]: 3,
};

const LAYOUT_SIZE_TO_ITEM_SEPARATOR_MARGIN_MAP: {
  [size in LayoutSize]: number;
} = {
  [LayoutSize.SMALL]: 16,
  [LayoutSize.SMALL_TABLET]: 16,
  [LayoutSize.MEDIUM]: 32,
  [LayoutSize.LARGE]: 32,
};

interface BaseProps {
  animated?: boolean;
  gridColumnCount?: number;
  hasExternalSeparatorControl?: boolean;
  highlightedItemValues?: ListOptionValue[];
  keyboardFocusedIndex?: number | null;
  keyExtractor?: (item: ItemListOption, index: number) => string;
  listContentContainerStyle?: StyleProp<ViewStyle>;
  ListFooterComponent?: RecyclerListProps<ItemListOption>['ListFooterComponent'];
  ListHeaderComponent?: RecyclerListProps<ItemListOption>['ListHeaderComponent'];
  listItemProps: Partial<ListItemProps<ItemListOption>>;
  listItemSeparatorStyle?: StyleProp<ViewStyle>;
  listRef?: React.Ref<RecyclerListRef<ItemListOption>>;
  listRowItemLayout?: boolean;
  onEndReached?: () => void;
  onViewableItemsChanged?: RecyclerListProps<ItemListOption>['onViewableItemsChanged'];
  onVisible?: () => void;
  renderListHeaderAsListItem?: boolean;
  renderSectionHeader?: RecyclerListProps<ItemListOption>['renderSectionHeader'];
  scrollViewProps?: ScrollViewProps;
  shouldRenderSeparator?: (
    leadingItem: ItemListOption,
    trailingItem: ItemListOption,
  ) => boolean;
  showListView?: boolean;
  showSeparatorBetweenSections?: boolean;
  skipInteractionCheck?: boolean;
  viewabilityConfig?: RecyclerListProps<ItemListOption>['viewabilityConfig'];
}

export const itemListItemKeyExtractor = (item: ItemListOption): string => {
  const skus = item.metadata?.skus ?? [];
  return [
    item.title,
    item.value,
    item.metadata?.orderGuidePartitionUUID ?? '',
    item.metadata?.unitPriceInMicros ?? 0,
    item.metadata?.paidSearch?.ad_campaign_id ?? '',
    skus.length,
    ...skus.flatMap((sku) => [
      sku.unitPriceInMicros ?? 0,
      sku.paidSearch?.ad_campaign_id,
      '',
    ]),
    ...(item.metadata?.outOfStockUUIDs ?? []),
  ].join('-');
};

export const useOrderableItemColumnCount = () => {
  const { showListView } = useShowListView();
  const columnCount = useLayoutSizeValue(LAYOUT_SIZE_TO_COLUMNS_MAP);

  return showListView ? 1 : columnCount;
};

type Props = BaseProps &
  (
    | {
        listData: ItemListOption[];
        sections?: undefined;
        stickyHeaderIndices?: undefined;
      }
    | {
        listData?: undefined;
        sections: ItemListOptionSection[];
        stickyHeaderIndices?: number[];
      }
  );

export const OrderableItemList: React.FC<Props> = ({
  animated,
  keyExtractor = itemListItemKeyExtractor,
  gridColumnCount,
  hasExternalSeparatorControl,
  highlightedItemValues,
  keyboardFocusedIndex,
  listContentContainerStyle,
  listData,
  ListFooterComponent,
  ListHeaderComponent,
  listItemProps,
  listItemSeparatorStyle,
  listRef,
  listRowItemLayout,
  onEndReached,
  onViewableItemsChanged,
  onVisible,
  renderListHeaderAsListItem,
  renderSectionHeader,
  scrollViewProps = {},
  sections,
  shouldRenderSeparator,
  showListView = false,
  stickyHeaderIndices,
  showSeparatorBetweenSections,
  viewabilityConfig,
  skipInteractionCheck,
}) => {
  const defaultColumnCount = useOrderableItemColumnCount();
  const columnCount = showListView ? 1 : gridColumnCount || defaultColumnCount;
  const useCompactListRowHeight =
    columnCount === 1 &&
    (listRowItemLayout !== undefined ? listRowItemLayout : showListView);

  const itemSeparatorMarginHorizontal = useLayoutSizeValue(
    LAYOUT_SIZE_TO_ITEM_SEPARATOR_MARGIN_MAP,
  );

  const memoizedListItemProps = useMemo(
    () => ({
      columnCount,
      listItemStyle: [
        styles.listItem,
        columnCount > 1 ? styles.multiColumnListItem : null,
      ],
      ...listItemProps,
    }),
    [columnCount, listItemProps],
  );

  const listItemSeparatorProps = useMemo(
    () => ({
      style: [
        styles.listItemSeparator,
        {
          marginHorizontal: itemSeparatorMarginHorizontal,
        },
        listItemSeparatorStyle,
      ],
    }),
    [itemSeparatorMarginHorizontal, listItemSeparatorStyle],
  );

  const listSections = useMemo(() => {
    return sections || [{ data: listData, key: null }];
  }, [listData, sections]);

  // Re-render list when list view changes or keyboard focus changes.
  // This is a limitation with RecyclerList, which does not properly re-render
  // list item render methods despite the underlying focus changing.
  // Quantity state is intentionally absent: rows subscribe to their own
  // entries through the quantity store, so a dispatch no longer re-renders
  // this wrapper and every visible row.
  const extraData = useMemo(
    () => ({
      highlightedItemValues,
      keyboardFocusedIndex,
      showListView,
    }),
    [highlightedItemValues, keyboardFocusedIndex, showListView],
  );

  const RecyclerListComponent: React.FC<
    RecyclerListProps<ItemListOption> & Partial<KeyboardAwareProps>
  > = animated ? AnimatedRecyclerList : KeyboardAwareRecyclerList;

  // Always hide list item separator for single column lists
  const finalShouldRenderSeparator =
    columnCount === 1 && !hasExternalSeparatorControl
      ? () => false
      : shouldRenderSeparator;
  const recyclerLayoutKey = `orderable-list-${columnCount}-${
    useCompactListRowHeight ? 'compact' : 'regular'
  }`;

  // On iOS, force remount when display mode changes to avoid stale FlashList
  // measurements after list <-> grid transitions.
  const shouldUseRecyclerLayoutKey = Platform.OS === 'ios';

  return (
    <RecyclerListComponent
      contentContainerStyle={listContentContainerStyle}
      estimatedItemSize={useCompactListRowHeight ? 90 : 160}
      extraData={extraData}
      highlightedItemValues={highlightedItemValues}
      key={shouldUseRecyclerLayoutKey ? recyclerLayoutKey : undefined}
      keyboardDismissMode={Platform.OS === 'web' ? undefined : 'on-drag'}
      keyboardShouldPersistTaps="handled"
      keyExtractor={keyExtractor}
      ListFooterComponent={ListFooterComponent}
      ListHeaderComponent={ListHeaderComponent}
      listItemProps={memoizedListItemProps}
      listItemSeparatorProps={listItemSeparatorProps}
      listRef={listRef}
      numColumns={columnCount}
      onEndReached={onEndReached}
      onViewableItemsChanged={onViewableItemsChanged}
      onVisible={onVisible}
      renderListHeaderAsListItem={renderListHeaderAsListItem}
      renderSectionHeader={renderSectionHeader}
      scrollEventThrottle={animated ? 16 : 100}
      sections={listSections}
      shouldRenderSeparator={finalShouldRenderSeparator}
      showSeparatorBetweenSections={showSeparatorBetweenSections}
      skipInteractionCheck={skipInteractionCheck}
      viewabilityConfig={viewabilityConfig}
      {...(animated ? {} : { extraSpacing: 40 })}
      {...(typeof stickyHeaderIndices === 'object'
        ? { stickyHeaderIndices }
        : {})}
      {...scrollViewProps}
    />
  );
};

const styles = StyleSheet.create({
  listItem: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  listItemSeparator: {
    marginTop: 'auto',
  },
  multiColumnListItem: {
    paddingHorizontal: 16,
  },
});
